import * as React from 'react';
import { UserContext } from '../context/userContext';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Collapse,
  IconButton,
  Paper,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LocalPoliceIcon from '@mui/icons-material/LocalPolice';
import { ConversationItemQa } from '../types';
import {
  ContactSme,
  FeedBack,
  FollowUpQuestions,
  InconclusiveAnswer,
  MuiMarkDown,
  References,
  TitleSection,
} from '.';
import { format } from 'date-fns';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLess from '@mui/icons-material/ExpandLess';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import TryIcon from '@mui/icons-material/Try';
import ContactMailIcon from '@mui/icons-material/ContactMail';

interface Props {
  data: ConversationItemQa;
  onFollowUpQuestion: (question: string) => void;
  conversationId: string;
  number: number;
  canContactSME: boolean;
  hideDiscussSME?: boolean;
  collapsed?: boolean;
}

export const ConversationQa: React.FC<Props> = ({
  data: {
    answer,
    question,
    uid: quid,
    source_documents,
    review_uid,
    follow_up_questions,
    annotations,
    feedback_lickert,
    feedback_comment,
    created_at,
  },
  onFollowUpQuestion,
  conversationId,
  number,
  canContactSME,
  hideDiscussSME,
  collapsed = false,
}) => {
  const { loggedIn } = React.useContext(UserContext);
  const [isExpanded, setIsExpanded] = React.useState(!collapsed);

  React.useEffect(() => {
    setIsExpanded(!collapsed);
  }, [collapsed]);

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  return loggedIn ? (
    <Stack key={quid} direction="row" gap={1}>
      {number}
      <Paper sx={{ width: '100%' }}>
        <Stack spacing={2} padding={2}>
          <Stack direction="row" justifyContent="space-between" pr={1.5}>
            <TitleSection
              title={question}
              icon={<AccountCircleIcon fontSize="large" />}
            ></TitleSection>
            <IconButton onClick={handleToggle} aria-label="expand" size="small">
              {isExpanded ? <ExpandLess /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
          {/* {isExpanded && <hr />} */}
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Grid container borderTop="1px solid #dfdfdf" pt={2}>
              <Grid size={6} lineHeight={2} pr={2}>
                <Stack gap={2}>
                  <TitleSection
                    title={format(created_at, 'MMMM do, yyyy HH:mm')}
                    icon={<LocalPoliceIcon fontSize="large" />}
                  >
                    <MuiMarkDown key={quid} mdContent={answer} />
                  </TitleSection>

                  <InconclusiveAnswer annotation={annotations} />
                </Stack>
              </Grid>
              <Grid size={6} pl={2} borderLeft="1px solid #dfdfdf">
                <Accordion elevation={0} disableGutters defaultExpanded>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <TitleSection
                      title="Follow up question(s):"
                      icon={<QuestionAnswerIcon fontSize="medium" />}
                      contentPadding="24px"
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <FollowUpQuestions
                      questions={follow_up_questions}
                      onClick={onFollowUpQuestion}
                    />
                  </AccordionDetails>
                </Accordion>
                <Accordion elevation={0} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <TitleSection
                      title={`References (${source_documents?.length || 0}) :`}
                      icon={<LibraryBooksIcon fontSize="medium" />}
                      contentPadding="32px"
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <References
                      cuid={conversationId}
                      quid={quid}
                      sourceDocuments={source_documents}
                    />
                  </AccordionDetails>
                </Accordion>
                <Accordion elevation={0} disableGutters>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <TitleSection
                      title="Review answer:"
                      icon={<TryIcon fontSize="medium" />}
                      contentPadding="32px"
                    />
                  </AccordionSummary>
                  <AccordionDetails>
                    <FeedBack
                      uid={conversationId}
                      quid={quid}
                      defaultValue={feedback_lickert}
                      defaultComment={feedback_comment}
                    />
                  </AccordionDetails>
                </Accordion>
                {!hideDiscussSME && (
                  <Accordion elevation={0} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <TitleSection
                        title="Discuss answer with Subject Matter Expert:"
                        icon={<ContactMailIcon fontSize="medium" />}
                        contentPadding="32px"
                      />
                    </AccordionSummary>
                    <AccordionDetails>
                      <ContactSme
                        canContactSME={canContactSME}
                        quid={quid}
                        reviewUid={review_uid}
                      />
                    </AccordionDetails>
                  </Accordion>
                )}
              </Grid>
            </Grid>
          </Collapse>
        </Stack>
      </Paper>
    </Stack>
  ) : null;
};
